import React, { useState } from 'react';
import { UserPlus, Shield, ArrowLeft, Sparkles, Mail, Eye, EyeOff, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { validateRegisterForm, getPasswordStrength } from '../utils/validation';
import { sendVerificationCode, verifyCode } from '../utils/emailVerification';
interface PatientSignupPageProps {
  onBackToLogin: () => void;
}
export function PatientSignupPage({
  onBackToLogin
}: PatientSignupPageProps) {
  const {
    register
  } = useAuth();
  const [step, setStep] = useState<'form' | 'verify'>('form');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [registerError, setRegisterError] = useState('');
  const [verificationError, setVerificationError] = useState('');
  const passwordStrength = password ? getPasswordStrength(password) : null;
  const strengthColors = {
    weak: 'bg-red-400',
    medium: 'bg-amber-400',
    strong: 'bg-emerald-400',
    very_strong: 'bg-emerald-500'
  };
  const handleSendCode = async () => {
    setRegisterError('');
    setVerificationError('');
    const validation = validateRegisterForm(name, email, password, confirmPassword);
    if (!validation.isValid) {
      setErrors(validation.errors);
      return;
    }
    setErrors({});
    setIsSendingCode(true);
    const result = await sendVerificationCode(email);
    if (result.success) {
      setStep('verify');
    } else {
      setRegisterError(result.error || 'Error al enviar código de verificación');
    }
    setIsSendingCode(false);
  };
  const handleVerifyCode = async () => {
    setVerificationError('');
    if (!verificationCode || verificationCode.length !== 6) {
      setVerificationError('Por favor ingresa el código de 6 dígitos');
      return;
    }
    setIsVerifying(true);
    const result = await verifyCode(email, verificationCode);
    if (result.success) {
      await handleRegister();
    } else {
      setVerificationError(result.error || 'Código incorrecto');
    }
    setIsVerifying(false);
  };
  const handleRegister = async () => {
    setIsLoading(true);
    const result = await register(name, email, password, 'paciente');
    if (!result.success) {
      setRegisterError(result.error || 'Error al crear la cuenta');
      setStep('form');
    }
    setIsLoading(false);
  };
  const handleResendCode = async () => {
    setVerificationError('');
    setIsSendingCode(true);
    const result = await sendVerificationCode(email);
    if (result.success) {
      setVerificationError('');
      setVerificationCode('');
    } else {
      setVerificationError(result.error || 'Error al reenviar código');
    }
    setIsSendingCode(false);
  };
  // Verification Step
  if (step === 'verify') {
    return <div className="min-h-screen bg-gradient-to-br from-cyan-50/50 via-white to-teal-50/30 flex items-center justify-center p-4" data-id="element-5750">
        <div className="w-full max-w-md" data-id="element-5751">
          <button onClick={() => setStep('form')} className="mb-8 flex items-center gap-2 text-gray-500 hover:text-teal-600 transition-colors group" data-id="element-5752">
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" data-id="element-5753" />
            <span className="text-sm font-medium" data-id="element-5754">Volver</span>
          </button>

          <div className="bg-white rounded-3xl shadow-lg shadow-teal-100/50 p-8 sm:p-10 border border-gray-100" data-id="element-5755">
            <div className="text-center mb-8" data-id="element-5756">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-2xl shadow-lg shadow-teal-200/50 mb-6" data-id="element-5757">
                <Mail className="w-8 h-8 text-white" strokeWidth={2.5} data-id="element-5758" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-5759">
                Verifica tu correo
              </h2>
              <p className="text-sm text-gray-600 mb-1" data-id="element-5760">Código enviado a</p>
              <p className="text-sm font-semibold text-teal-600" data-id="element-5761">{email}</p>
            </div>

            {verificationError && <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-2xl" data-id="element-5762">
                <p className="text-sm text-red-700 font-medium" data-id="element-5763">
                  {verificationError}
                </p>
              </div>}

            <div className="space-y-6" data-id="element-5764">
              <div data-id="element-5765">
                <label className="block text-sm font-semibold text-gray-700 mb-3" data-id="element-5766">
                  Código de Verificación
                </label>
                <input type="text" maxLength={6} value={verificationCode} onChange={e => setVerificationCode(e.target.value.replace(/\D/g, ''))} className="w-full px-4 py-4 text-center text-2xl font-bold tracking-[0.5em] border-2 border-gray-200 rounded-2xl focus:ring-4 focus:ring-teal-100 focus:border-teal-500 transition-all outline-none" placeholder="000000" autoFocus data-id="element-5767" />
                <p className="text-xs text-gray-500 mt-2 text-center" data-id="element-5768">
                  El código expira en 10 minutos
                </p>
              </div>

              <button onClick={handleVerifyCode} disabled={isVerifying || verificationCode.length !== 6} className="w-full h-12 text-base font-semibold bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white shadow-lg shadow-teal-200/50 rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-5769">
                {isLoading ? 'Creando cuenta...' : isVerifying ? 'Verificando...' : 'Verificar y Crear Cuenta'}
              </button>

              <div className="text-center pt-2" data-id="element-5770">
                <p className="text-sm text-gray-600 mb-2" data-id="element-5771">
                  ¿No recibiste el código?
                </p>
                <button onClick={handleResendCode} disabled={isSendingCode} className="text-sm text-teal-600 font-semibold hover:text-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors" data-id="element-5772">
                  {isSendingCode ? 'Reenviando...' : 'Reenviar código'}
                </button>
              </div>
            </div>
          </div>

          <div className="mt-6 flex items-center justify-center gap-2 text-xs text-gray-500" data-id="element-5773">
            <Shield className="w-4 h-4 text-teal-600" data-id="element-5774" />
            <span data-id="element-5775">Verificación segura de correo electrónico</span>
          </div>
        </div>
      </div>;
  }
  // Registration Form Step
  return <div className="min-h-screen bg-gradient-to-br from-cyan-50/50 via-white to-teal-50/30 flex items-center justify-center p-4" data-id="element-5776">
      <div className="w-full max-w-md" data-id="element-5777">
        <button onClick={onBackToLogin} className="mb-8 flex items-center gap-2 text-gray-500 hover:text-teal-600 transition-colors group" data-id="element-5778">
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" data-id="element-5779" />
          <span className="text-sm font-medium" data-id="element-5780">
            Volver al inicio de sesión
          </span>
        </button>

        <div className="text-center mb-8" data-id="element-5781">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-2xl shadow-lg shadow-teal-200/50 mb-6" data-id="element-5782">
            <UserPlus className="w-8 h-8 text-white" strokeWidth={2.5} data-id="element-5783" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-5784">
            Crear Cuenta de Paciente
          </h1>
          <div className="flex items-center justify-center gap-2 text-teal-600" data-id="element-5785">
            
            <p className="text-sm font-medium" data-id="element-5786">Sistema Savidna</p>
            
          </div>
        </div>

        <div className="bg-white rounded-3xl shadow-lg shadow-teal-100/50 p-8 sm:p-10 border border-gray-100" data-id="element-5787">
          <div className="mb-8" data-id="element-5788">
            <h2 className="text-xl font-bold text-gray-900 mb-2" data-id="element-5789">Bienvenido</h2>
            <p className="text-sm text-gray-600" data-id="element-5790">
              Crea tu cuenta para registrar tu información médica de forma
              segura
            </p>
          </div>

          {registerError && <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-2xl" data-id="element-5791">
              <p className="text-sm text-red-700 font-medium" data-id="element-5792">
                {registerError}
              </p>
            </div>}

          <form onSubmit={e => {
          e.preventDefault();
          handleSendCode();
        }} className="space-y-5" data-id="element-5793">
            <div data-id="element-5794">
              <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-5795">
                Nombre Completo
              </label>
              <input type="text" placeholder="Juan Pérez" value={name} onChange={e => setName(e.target.value)} className={`w-full px-4 py-3 border-2 rounded-xl transition-all outline-none focus:ring-4 focus:ring-teal-100 focus:border-teal-500 ${errors.name ? 'border-red-300 bg-red-50/50' : 'border-gray-200 bg-white hover:border-gray-300'}`} data-id="element-5796" />
              {errors.name && <p className="text-xs text-red-600 mt-1.5 flex items-center gap-1" data-id="element-5797">
                  {errors.name}
                </p>}
            </div>

            <div data-id="element-5798">
              <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-5799">
                Correo Electrónico
              </label>
              <input type="email" placeholder="correo@ejemplo.com" value={email} onChange={e => setEmail(e.target.value)} className={`w-full px-4 py-3 border-2 rounded-xl transition-all outline-none focus:ring-4 focus:ring-teal-100 focus:border-teal-500 ${errors.email ? 'border-red-300 bg-red-50/50' : 'border-gray-200 bg-white hover:border-gray-300'}`} data-id="element-5800" />
              {errors.email && <p className="text-xs text-red-600 mt-1.5" data-id="element-5801">{errors.email}</p>}
            </div>

            <div data-id="element-5802">
              <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-5803">
                Contraseña
              </label>
              <div className="relative" data-id="element-5804">
                <input type={showPassword ? 'text' : 'password'} placeholder="••••••••" value={password} onChange={e => setPassword(e.target.value)} className={`w-full px-4 py-3 pr-11 border-2 rounded-xl transition-all outline-none focus:ring-4 focus:ring-teal-100 focus:border-teal-500 ${errors.password ? 'border-red-300 bg-red-50/50' : 'border-gray-200 bg-white hover:border-gray-300'}`} data-id="element-5805" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors p-1" data-id="element-5806">
                  {showPassword ? <EyeOff className="w-5 h-5" data-id="element-5807" /> : <Eye className="w-5 h-5" data-id="element-5808" />}
                </button>
              </div>
              {errors.password && <p className="text-xs text-red-600 mt-1.5" data-id="element-5809">{errors.password}</p>}
              {password && passwordStrength && <div className="mt-3" data-id="element-5810">
                  <div className="flex items-center gap-2 mb-1" data-id="element-5811">
                    <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden" data-id="element-5812">
                      <div className={`h-full rounded-full transition-all duration-500 ${strengthColors[passwordStrength.level]}`} style={{
                    width: `${Math.min(100, passwordStrength.score / 7 * 100)}%`
                  }} data-id="element-5813" />
                    </div>
                    <span className={`text-xs font-semibold ${passwordStrength.level === 'weak' ? 'text-red-500' : passwordStrength.level === 'medium' ? 'text-amber-500' : 'text-emerald-500'}`} data-id="element-5814">
                      {passwordStrength.label}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500" data-id="element-5815">
                    Mín. 8 caracteres, mayúscula, minúscula, número y símbolo
                  </p>
                </div>}
            </div>

            <div data-id="element-5816">
              <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-5817">
                Confirmar Contraseña
              </label>
              <div className="relative" data-id="element-5818">
                <input type={showConfirmPassword ? 'text' : 'password'} placeholder="••••••••" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className={`w-full px-4 py-3 pr-11 border-2 rounded-xl transition-all outline-none focus:ring-4 focus:ring-teal-100 focus:border-teal-500 ${errors.confirmPassword ? 'border-red-300 bg-red-50/50' : 'border-gray-200 bg-white hover:border-gray-300'}`} data-id="element-5819" />
                <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors p-1" data-id="element-5820">
                  {showConfirmPassword ? <EyeOff className="w-5 h-5" data-id="element-5821" /> : <Eye className="w-5 h-5" data-id="element-5822" />}
                </button>
              </div>
              {errors.confirmPassword && <p className="text-xs text-red-600 mt-1.5" data-id="element-5823">
                  {errors.confirmPassword}
                </p>}
            </div>

            <div className="flex items-start gap-3 p-4 bg-gradient-to-br from-teal-50/80 to-cyan-50/50 border border-teal-100 rounded-2xl" data-id="element-5824">
              <Shield className="w-5 h-5 text-teal-600 mt-0.5 flex-shrink-0" data-id="element-5825" />
              <div data-id="element-5826">
                <p className="text-xs text-teal-900 font-semibold mb-0.5" data-id="element-5827">
                  Información Segura y Confidencial
                </p>
                <p className="text-xs text-teal-700 leading-relaxed" data-id="element-5828">
                  Tu contraseña se almacena de forma segura con cifrado SHA-256.
                  Toda tu información médica está protegida.
                </p>
              </div>
            </div>

            <button type="submit" disabled={isSendingCode} className="w-full h-12 text-base font-semibold bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white shadow-lg shadow-teal-200/50 rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-5829">
              {isSendingCode ? 'Enviando código...' : 'Continuar'}
            </button>
          </form>

          <div className="mt-6 text-center" data-id="element-5830">
            <p className="text-sm text-gray-600" data-id="element-5831">
              ¿Ya tienes una cuenta?{' '}
              <button onClick={onBackToLogin} className="text-teal-600 font-semibold hover:text-teal-700 transition-colors" data-id="element-5832">
                Iniciar sesión
              </button>
            </p>
          </div>
        </div>

        <p className="text-center text-xs text-gray-500 mt-6 leading-relaxed" data-id="element-5833">
          Al crear una cuenta, recibirás un código de verificación en tu correo
          para confirmar tu identidad.
        </p>
      </div>
    </div>;
}